import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, query } from 'firebase/firestore';
import HitsterCard from '../components/HitsterCard';
import './Hitster.css';

function Hitster({ user }) {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState('artist'); // 'artist' or 'year'
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedArtists, setExpandedArtists] = useState(new Set());
  const [expandedYears, setExpandedYears] = useState(new Set());

  // Fetch all songs from albums
  useEffect(() => {
    const fetchAllSongs = async () => {
      setLoading(true);
      try {
        console.log('🎵 Fetching all songs for Hitster...');
        
        // Get all albums from albums-flat
        const albumsQuery = query(collection(db, 'albums-flat'));
        const albumsSnapshot = await getDocs(albumsQuery);
        
        const allSongs = [];
        
        albumsSnapshot.forEach((doc) => {
          const albumData = { id: doc.id, ...doc.data() };
          
          // Skip unavailable albums
          if (albumData.storageStatus === 'unavailable') {
            return;
          }
          
          // Extract songs from album
          if (albumData.songs && Array.isArray(albumData.songs)) {
            albumData.songs.forEach((song) => {
              // Extract year from album name if available (e.g., "1971 - Ben")
              let year = null;
              if (albumData.album) {
                const yearMatch = albumData.album.match(/\[(\d{4})\]/);
                if (yearMatch) {
                  year = parseInt(yearMatch[1]);
                } else {
                  // Try to extract from album name like "1971 - Ben"
                  const yearMatch2 = albumData.album.match(/^(\d{4})/);
                  if (yearMatch2) {
                    year = parseInt(yearMatch2[1]);
                  }
                }
              }
              
              // Also check if song has a year field
              if (!year && song.year) {
                year = parseInt(song.year);
              }
              
              allSongs.push({
                ...song,
                albumId: albumData.id,
                albumName: albumData.album || albumData.albumName,
                artist: albumData.artist || albumData.artistName || 'Unknown Artist',
                year: year || song.year || null,
                language: albumData.language,
                genre: albumData.genre,
                type: albumData.type || 'Albums',
              });
            });
          }
        });
        
        console.log(`✅ Found ${allSongs.length} songs from ${albumsSnapshot.size} albums`);
        setSongs(allSongs);
      } catch (error) {
        console.error('❌ Error fetching songs:', error);
        alert(`Error loading songs: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchAllSongs();
    }
  }, [user]);

  // Filter songs based on search query
  const filteredSongs = songs.filter((song) => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const title = (song.DisplayTitle || song.Title || '').toLowerCase();
    const artist = (song.artist || '').toLowerCase();
    const album = (song.albumName || '').toLowerCase();
    
    return title.includes(query) || artist.includes(query) || album.includes(query);
  });

  // Group songs by artist
  const songsByArtist = filteredSongs.reduce((acc, song) => {
    const artist = song.artist || 'Unknown Artist';
    if (!acc[artist]) {
      acc[artist] = [];
    }
    acc[artist].push(song);
    return acc;
  }, {});

  // Group songs by year
  const songsByYear = filteredSongs.reduce((acc, song) => {
    const year = song.year || 'Unknown Year';
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(song);
    return acc;
  }, {});

  // Sort artists alphabetically
  const sortedArtists = Object.keys(songsByArtist).sort();

  // Sort years numerically (descending, with Unknown Year last)
  const sortedYears = Object.keys(songsByYear).sort((a, b) => {
    if (a === 'Unknown Year') return 1;
    if (b === 'Unknown Year') return -1;
    return parseInt(b) - parseInt(a); // Descending order
  });

  // Toggle artist expansion
  const toggleArtist = (artist) => {
    const newExpanded = new Set(expandedArtists);
    if (newExpanded.has(artist)) {
      newExpanded.delete(artist);
    } else {
      newExpanded.add(artist);
    }
    setExpandedArtists(newExpanded);
  };

  // Toggle year expansion
  const toggleYear = (year) => {
    const newExpanded = new Set(expandedYears);
    if (newExpanded.has(year)) {
      newExpanded.delete(year);
    } else {
      newExpanded.add(year);
    }
    setExpandedYears(newExpanded);
  };

  if (loading) {
    return (
      <div className="hitster-container">
        <div className="hitster-loading">
          <div className="loading-spinner">🎵</div>
          <p>Loading all songs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hitster-container">
      <div className="hitster-header">
        <h1>🎯 Hitster - All Songs</h1>
        <p className="hitster-subtitle">
          {filteredSongs.length} song{filteredSongs.length !== 1 ? 's' : ''} total
        </p>
      </div>

      <div className="hitster-controls">
        <div className="hitster-search">
          <input
            type="text"
            placeholder="Search songs, artists, or albums..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="hitster-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="hitster-search-clear"
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        <div className="hitster-group-toggle">
          <button
            className={`hitster-toggle-btn ${groupBy === 'artist' ? 'active' : ''}`}
            onClick={() => setGroupBy('artist')}
          >
            By Artist
          </button>
          <button
            className={`hitster-toggle-btn ${groupBy === 'year' ? 'active' : ''}`}
            onClick={() => setGroupBy('year')}
          >
            By Year
          </button>
        </div>
      </div>

      <div className="hitster-content">
        {groupBy === 'artist' ? (
          <div className="hitster-artist-view">
            {sortedArtists.length === 0 ? (
              <div className="hitster-empty">
                <p>No songs found</p>
              </div>
            ) : (
              sortedArtists.map((artist) => {
                const artistSongs = songsByArtist[artist];
                const isExpanded = expandedArtists.has(artist);
                
                // Group songs by year within artist
                const songsByYearInArtist = artistSongs.reduce((acc, song) => {
                  const year = song.year || 'Unknown Year';
                  if (!acc[year]) {
                    acc[year] = [];
                  }
                  acc[year].push(song);
                  return acc;
                }, {});
                
                const yearsInArtist = Object.keys(songsByYearInArtist).sort((a, b) => {
                  if (a === 'Unknown Year') return 1;
                  if (b === 'Unknown Year') return -1;
                  return parseInt(b) - parseInt(a);
                });

                return (
                  <div key={artist} className="hitster-artist-section">
                    <div
                      className="hitster-artist-header"
                      onClick={() => toggleArtist(artist)}
                    >
                      <span className="hitster-expand-icon">
                        {isExpanded ? '▼' : '▶'}
                      </span>
                      <span className="hitster-artist-name">{artist}</span>
                      <span className="hitster-song-count">
                        {artistSongs.length} song{artistSongs.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {isExpanded && (
                      <div className="hitster-artist-songs">
                        {yearsInArtist.map((year) => {
                          const yearSongs = songsByYearInArtist[year];
                          return (
                            <div key={year} className="hitster-year-group">
                              <div className="hitster-year-header">
                                <span className="hitster-year-label">{year}</span>
                                <span className="hitster-year-count">
                                  {yearSongs.length} song{yearSongs.length !== 1 ? 's' : ''}
                                </span>
                              </div>
                              <div className="hitster-cards-grid">
                                {yearSongs
                                  .sort((a, b) => {
                                    const titleA = (a.DisplayTitle || a.Title || '').toLowerCase();
                                    const titleB = (b.DisplayTitle || b.Title || '').toLowerCase();
                                    return titleA.localeCompare(titleB);
                                  })
                                  .map((song, index) => (
                                    <HitsterCard
                                      key={`${song.albumId}-${song.Id || song.id || index}`}
                                      artist={song.artist || 'Unknown Artist'}
                                      year={song.year || '?'}
                                      songTitle={song.DisplayTitle || song.Title || 'Unknown Song'}
                                      trackUri={song.spotifyUri || song.uri}
                                      trackId={song.spotifyId || song.id}
                                      size="medium"
                                    />
                                  ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        ) : (
          <div className="hitster-year-view">
            {sortedYears.length === 0 ? (
              <div className="hitster-empty">
                <p>No songs found</p>
              </div>
            ) : (
              sortedYears.map((year) => {
                const yearSongs = songsByYear[year];
                const isExpanded = expandedYears.has(year);
                
                // Group songs by artist within year
                const songsByArtistInYear = yearSongs.reduce((acc, song) => {
                  const artist = song.artist || 'Unknown Artist';
                  if (!acc[artist]) {
                    acc[artist] = [];
                  }
                  acc[artist].push(song);
                  return acc;
                }, {});
                
                const artistsInYear = Object.keys(songsByArtistInYear).sort();

                return (
                  <div key={year} className="hitster-year-section">
                    <div
                      className="hitster-year-header-main"
                      onClick={() => toggleYear(year)}
                    >
                      <span className="hitster-expand-icon">
                        {isExpanded ? '▼' : '▶'}
                      </span>
                      <span className="hitster-year-label-main">{year}</span>
                      <span className="hitster-song-count">
                        {yearSongs.length} song{yearSongs.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {isExpanded && (
                      <div className="hitster-year-songs">
                        {artistsInYear.map((artist) => {
                          const artistSongs = songsByArtistInYear[artist];
                          return (
                            <div key={artist} className="hitster-artist-group">
                              <div className="hitster-artist-header-small">
                                <span className="hitster-artist-name-small">{artist}</span>
                                <span className="hitster-year-count">
                                  {artistSongs.length} song{artistSongs.length !== 1 ? 's' : ''}
                                </span>
                              </div>
                              <div className="hitster-songs-list">
                                {artistSongs
                                  .sort((a, b) => {
                                    const titleA = (a.DisplayTitle || a.Title || '').toLowerCase();
                                    const titleB = (b.DisplayTitle || b.Title || '').toLowerCase();
                                    return titleA.localeCompare(titleB);
                                  })
                                  .map((song, index) => (
                                    <div key={`${song.albumId}-${song.Id || song.id || index}`} className="hitster-song-item">
                                      <span className="hitster-song-title">
                                        {song.DisplayTitle || song.Title || 'Unknown Song'}
                                      </span>
                                      <span className="hitster-song-album">
                                        {song.albumName || 'Unknown Album'}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Hitster;

